import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// API helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  for (const base of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const cfg = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) cfg.body = JSON.stringify(data);
      const res = await fetch(base, cfg);
      if (!res) continue;
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json')) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text;
      }
    } catch (e) { /* try next base */ }
  }
  throw new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

// Extrai primeira letra válida (sem números ou caracteres especiais)
const getInitial = (name) => {
  if (!name) return '?';
  const clean = name.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  return clean.charAt(0).toUpperCase() || '?';
};

export default function GerenciarAdminPopup({ grupoId, donoId, isOwner, onClose, onAdminsChanged }) {
  const [filterTerm, setFilterTerm] = useState('');
  const [membros, setMembros] = useState([]);
  const [membrosLoading, setMembrosLoading] = useState(false);
  const [carrinho, setCarrinho] = useState([]); // membros selecionados para tornar admin
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Carregar membros atuais do grupo
  const carregarMembros = useCallback(async () => {
    if (!grupoId) return;
    setMembrosLoading(true);
    try {
      const res = await apiCall(`UsuariosGrupo/grupo/${grupoId}`, 'GET');
      const arr = Array.isArray(res) ? res : [];
      // Para cada membro, buscar dados do usuário e login
      const membrosComDados = await Promise.all(arr.map(async (m) => {
        try {
          const usId = m.UsuarioSistemaId || m.usuarioSistemaId;
          const dados = await apiCall(`DadosUsuario/por-usuario/${usId}`, 'GET');
          // Buscar login do UsuarioSistema
          let login = '';
          try {
            const usuarioSistema = await apiCall(`UsuarioSistema/${usId}`, 'GET');
            login = usuarioSistema?.Login || usuarioSistema?.login || '';
          } catch { /* ignora */ }
          return {
            ...m,
            nomeUsuario: dados?.NomeUsuario || dados?.nomeUsuario || dados?.Nome || dados?.nome || 'Usuário',
            usuarioSistemaId: usId,
            id: m.Id || m.id,
            adminGrupo: m.AdminGrupo || m.adminGrupo || 0,
            login: login
          };
        } catch {
          return { 
            ...m, 
            nomeUsuario: 'Usuário', 
            usuarioSistemaId: m.UsuarioSistemaId || m.usuarioSistemaId,
            id: m.Id || m.id,
            adminGrupo: m.AdminGrupo || m.adminGrupo || 0,
            login: ''
          };
        }
      }));
      setMembros(membrosComDados);
    } catch (err) {
      console.error('Erro ao carregar membros', err);
      setMembros([]);
    } finally {
      setMembrosLoading(false);
    }
  }, [grupoId]);

  useEffect(() => {
    carregarMembros();
  }, [carregarMembros]);

  // Filtrar membros que não são admin ainda
  // Admin não pode tornar admin outros admins ou o dono
  const membrosFiltrados = membros.filter(m => {
    const uid = m.usuarioSistemaId;
    // Somente membros que ainda não são admin
    if (m.adminGrupo === 1) return false;
    // Nunca mostrar o dono (dono já é automaticamente "super admin")
    if (uid === donoId) return false;
    // Se não é owner (é admin), não pode promover outros admins (já filtrado acima)
    if (!filterTerm) return true;
    const nome = m.nomeUsuario || '';
    return nome.toLowerCase().includes(filterTerm.toLowerCase());
  });

  // Membros que já são admin
  const adminsAtuais = membros.filter(m => m.adminGrupo === 1);

  // Toggle seleção no carrinho
  const toggleCarrinho = (membro) => {
    const id = membro.id;
    if (carrinho.find(c => c.id === id)) {
      setCarrinho(prev => prev.filter(c => c.id !== id));
    } else {
      setCarrinho(prev => [...prev, membro]);
    }
  };

  // Verificar se está no carrinho
  const estaNoCarrinho = (membro) => {
    return carrinho.find(c => c.id === membro.id) !== undefined;
  };

  // Tornar admins
  const tornarAdmins = async () => {
    if (carrinho.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const ids = carrinho.map(c => c.id);
      await apiCall('UsuariosGrupo/tornar-admin', 'PUT', ids);
      setCarrinho([]);
      await carregarMembros();
      if (onAdminsChanged) onAdminsChanged();
    } catch (err) {
      console.error('Erro ao tornar admin', err);
      setError('Erro ao tornar usuários admin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="membros-popup-overlay" onClick={(e) => { if (e.target.classList.contains('membros-popup-overlay')) onClose && onClose(); }}>
      <div className="membros-popup-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Gerenciar Administradores</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar" style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(235, 133, 0)' }}>×</button>
        </div>

        {/* Filtro */}
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            className="form-input"
            placeholder="Filtrar membros..."
            value={filterTerm}
            onChange={(e) => setFilterTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        {/* Aviso para admin */}
        {!isOwner && (
          <div style={{ background: '#fff3e0', padding: 10, borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'rgb(184, 98, 0)' }}>
            ⚠️ Como administrador, você só pode promover membros comuns a administradores. O dono do grupo e outros administradores não aparecem nesta lista.
          </div>
        )}

        {/* Admins atuais */}
        {adminsAtuais.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#333' }}>Administradores Atuais</h4>
            <div className="membros-lista-container" style={{ maxHeight: 120 }}>
              {adminsAtuais.map(m => (
                <div key={m.id} className="membros-lista-item">
                  <div className="membros-avatar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff' }}>
                    {getInitial(m.nomeUsuario)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <span className="membros-nome">{m.nomeUsuario}</span>
                    {m.login && <span style={{ fontSize: 12, color: 'rgb(235, 133, 0)' }}>{m.login}</span>}
                  </div>
                  <span className="membros-admin-badge">Admin</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Membros disponíveis para tornar admin */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#333' }}>Selecionar Novos Administradores</h4>
          <div className="membros-lista-container">
            {membrosLoading && <div style={{ color: '#666', padding: 8 }}>Carregando membros...</div>}
            {!membrosLoading && membrosFiltrados.length === 0 && (
              <div style={{ color: '#999', padding: 8 }}>Nenhum membro disponível</div>
            )}
            {!membrosLoading && membrosFiltrados.map(m => {
              const selecionado = estaNoCarrinho(m);
              return (
                <div
                  key={m.id}
                  className={`membros-lista-item selectable ${selecionado ? 'selected' : ''}`}
                  onClick={() => toggleCarrinho(m)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="membros-avatar" style={{ backgroundColor: selecionado ? '#4caf50' : 'rgb(235, 133, 0)', color: '#fff' }}>
                    {getInitial(m.nomeUsuario)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <span className="membros-nome">{m.nomeUsuario}</span>
                    {m.login && <span style={{ fontSize: 12, color: 'rgb(235, 133, 0)' }}>{m.login}</span>}
                  </div>
                  {selecionado && <span style={{ marginLeft: 'auto', color: '#4caf50' }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Carrinho de selecionados */}
        {carrinho.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="membros-carrinho">
              {carrinho.map(c => (
                <div key={c.id} className="membros-carrinho-item">
                  <div className="membros-avatar-small" style={{ backgroundColor: '#4caf50', color: '#fff' }}>
                    {getInitial(c.nomeUsuario)}
                  </div>
                  <span className="membros-carrinho-nome">{c.nomeUsuario}</span>
                  <button
                    className="membros-carrinho-remove"
                    onClick={(e) => { e.stopPropagation(); toggleCarrinho(c); }}
                    aria-label="Remover"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              className="button-create"
              onClick={tornarAdmins}
              disabled={saving}
              style={{ marginTop: 12, width: '100%' }}
            >
              {saving ? 'Salvando...' : carrinho.length === 1 ? 'Tornar Admin' : 'Tornar Admins'}
            </button>
          </div>
        )}

        {error && <div style={{ color: '#d32f2f', marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}
